import { action, computed, makeObservable } from 'mobx';
import { Geometry } from '../types/Geometry';
import { SearchQueryParams } from '../types/SearchQuery';
import FieldState from '../../common/store/FieldState';

type MapboxBounds = [
  [swlon: number, swlat: number],
  [nelon: number, nelat: number],
];

/**
 * Constrains coordinates to be compatible with mapbox.
 * Handles cases where the map projection spans the antimeridian.
 */
export const parametersToBounds = (parameters: Geometry): MapboxBounds | null => {
  if (!parameters) {
    return null;
  }
  const {
    swlon, swlat, nelon, nelat,
  } = parameters;

  if ([swlon, swlat, nelon, nelat].some((x) => typeof x !== 'number')) {
    return null;
  }

  // If the map viewport overlaps the international dateline (-180°/180°), then we need to
  // adjust the longitude coordinates so that they have the same sign for the initial render
  // Mapbox doesn't render the initial location correctly when the signs of the longitude
  // coordinates are not the same.
  if (swlon > nelon) {
    return [
      [(swlon + 360) % 360, swlat], // sw
      [(nelon + 360) % 360, nelat], // ne
    ];
  }
  return [
    [swlon, swlat], // sw
    [nelon, nelat], // ne
  ];
};

/**
 * Rounds the precision to 5 decimals and truncates trailing zeroes.
 */
const normalizeCoordinate = (value: number): string => value.toFixed(5).replace(/\.?0+$/, '');

type GeometryParams = {
  nelat: string;
  nelon: string;
  swlat: string;
  swlon: string;
};

const GEOMETRY_KEYS: (keyof Geometry)[] = ['nelat', 'nelon', 'swlat', 'swlon'];

/**
 * Converts all value properties on the geometry object to strings.
 */
export const toString = (geometry: Partial<Geometry>): Partial<GeometryParams> => (
  geometry ? Object.keys(geometry).reduce(
    (newGeometry: GeometryParams, key: keyof Geometry) => {
      if (GEOMETRY_KEYS.includes(key)) {
        // eslint-disable-next-line no-param-reassign
        newGeometry[key] = (geometry[key] != null ? normalizeCoordinate(geometry[key]) : null);
      }
      return newGeometry;
    },
    {} as GeometryParams,
  ) : {});

/**
 * Converts all value properties on the geometry object to numbers.
 */
export const toNumber = (geometry: GeometryParams): Geometry | null => (
  geometry ? Object.keys(geometry).reduce(
    (newGeometry: Geometry, key: keyof Geometry) => {
      if (GEOMETRY_KEYS.includes(key)) {
        // eslint-disable-next-line no-param-reassign
        newGeometry[key] = (geometry[key] != null ? Number(geometry[key]) : null);
      }
      return newGeometry;
    },
    {} as Geometry,
  ) : null);

const isGeometryParams = (geometry: any): geometry is GeometryParams => (
  geometry
  && GEOMETRY_KEYS.every(
    (key) => typeof geometry[key] === 'string',
  )
);

const isGeometry = (geometry: any): geometry is Geometry => (
  geometry
  && GEOMETRY_KEYS.every(
    (key) => typeof geometry[key] === 'number',
  )
);

/**
 * Ensures all the required values are available before parsing them to geometry.
 * If any of the values are missing, the geometry is set to `null`.
 */
export const validateGeometry = (geometry?: GeometryParams | Geometry | null): Geometry | null => {
  if (isGeometryParams(geometry)) {
    return toNumber(geometry);
  }
  if (isGeometry(geometry)) {
    return geometry;
  }
  return null;
};

/**
 * Parses the geometry from the query and will fallback to the initial geometry
 * that is included on location based pages.
 */
export const determineGeometry = (
  geometry: SearchQueryParams,
  initialGeometry: Geometry = {} as Geometry,
): GeometryParams => {
  const {
    nelat = null,
    nelon = null,
    swlat = null,
    swlon = null,
  } = initialGeometry;

  return {
    nelat: geometry.nelat ?? (nelat != null ? `${nelat}` : null),
    nelon: geometry.nelon ?? (nelon != null ? `${nelon}` : null),
    swlat: geometry.swlat ?? (swlat != null ? `${swlat}` : null),
    swlon: geometry.swlon ?? (swlon != null ? `${swlon}` : null),
  };
};

/**
 * Stores the mapbox geometry data.
 */
class GeometryFieldState extends FieldState<Geometry | null> {
  constructor(geometry: GeometryParams | null = null) {
    super(validateGeometry(geometry));
    makeObservable(this, {
      fitBounds: computed,
      query: computed,
      reset: action,
    });
  }

  /**
   * Resets the value using the string based interface.
   */
  public reset = (geometry: GeometryParams | Geometry | null = null): void => {
    this.onChange(validateGeometry(geometry));
  };

  /**
   * Formats the parameters to be passed to mapbox.
   */
  public get fitBounds(): MapboxBounds | null {
    return parametersToBounds(this.value);
  }

  /**
   * Converts all the numeric values to strings to be used in creating the
   * query string params.
   */
  public get query(): Partial<GeometryParams> {
    return toString(this.value);
  }
}

export default GeometryFieldState;
